import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  FormControl,
  Button,
  InputLabel,
  OutlinedInput,
  CircularProgress,
  Alert,
  Stack,
  Grid,
  Card,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import Header from "../Components/Utils/Header";
import Footer from "../Components/Utils/Footer";
import video from "../assets/videos/logo.mp4";

const CONTACT_INFO = [
  {
    icon: EmailIcon,
    title: "Email",
    details: "info@jeremysolutions.com",
    color: "primary",
  },
  {
    icon: PhoneIcon,
    title: "Phone",
    details: "+254 700 000 000",
    color: "success",
  },
  {
    icon: LocationIcon,
    title: "Location",
    details: "Nairobi, Kenya",
    color: "error",
  },
];

export default function ContactPage() {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await fetch("/api/messages/create", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(
          "Thank you for contacting Jeremy Solutions. We will respond as soon as possible."
        );
        setFormData({
          name: "",
          email: "",
          subject: "",
          message: "",
        });
      } else {
        throw new Error("Message not delivered");
      }
    } catch (err) {
      setError("Message not delivered. Please try again.");
    } finally {
      setLoading(false);
      setTimeout(() => {
        setSuccess("");
        setError("");
      }, 5000);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <Header active="Contact" />

      <Box
        sx={{
          pt: { xs: 12, md: 16 },
          pb: { xs: 8, md: 12 },
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "100%",
            background: `linear-gradient(180deg, ${alpha(
              theme.palette.primary.main,
              0.04
            )} 0%, transparent 100%)`,
          },
        }}
      >
        <Container>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Stack spacing={4}>
                <Box
                  component="video"
                  src={video}
                  autoPlay
                  loop
                  muted
                  playsInline
                  sx={{
                    width: "100%",
                    height: "auto",
                    borderRadius: 4,
                    boxShadow: theme.shadows[8],
                  }}
                />

                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  Get in Touch
                </Typography>

                <Typography variant="body1" sx={{ color: "text.secondary" }}>
                  We respond within 1-2 business days. For urgent matters,
                  please use phone contact.
                </Typography>

                <Grid container spacing={2}>
                  {CONTACT_INFO.map((info) => (
                    <Grid item xs={12} sm={4} key={info.title}>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                      >
                        <Card
                          elevation={0}
                          sx={{
                            p: 2,
                            height: "100%",
                            transition: "all 0.3s ease-in-out",
                            border: `1px solid ${alpha(
                              theme.palette[info.color].main,
                              0.1
                            )}`,
                            "&:hover": {
                              transform: "translateY(-4px)",
                              boxShadow: theme.shadows[4],
                            },
                          }}
                        >
                          <Stack
                            spacing={2}
                            alignItems="center"
                            textAlign="center"
                          >
                            <Box
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: alpha(
                                  theme.palette[info.color].main,
                                  0.1
                                ),
                                color: `${info.color}.main`,
                              }}
                            >
                              <info.icon />
                            </Box>
                            <Box>
                              <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 600 }}
                              >
                                {info.title}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {info.details}
                              </Typography>
                            </Box>
                          </Stack>
                        </Card>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                sx={{
                  p: 4,
                  height: "100%",
                  borderRadius: 4,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                }}
              >
                <Stack spacing={3}>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    Send Message
                  </Typography>

                  <form onSubmit={handleSubmit}>
                    <Stack spacing={3}>
                      {[
                        { name: "name", label: "Name", type: "text" },
                        { name: "email", label: "Email", type: "email" },
                        { name: "subject", label: "Subject", type: "text" },
                        {
                          name: "message",
                          label: "Message",
                          type: "text",
                          multiline: true,
                          rows: 4,
                        },
                      ].map((field) => (
                        <FormControl key={field.name} fullWidth>
                          <InputLabel>{field.label}</InputLabel>
                          <OutlinedInput
                            {...field}
                            value={formData[field.name]}
                            onChange={handleChange}
                            required
                          />
                        </FormControl>
                      ))}

                      <Button
                        variant="contained"
                        size="large"
                        type="submit"
                        disabled={loading}
                        endIcon={
                          loading ? (
                            <CircularProgress size={20} />
                          ) : (
                            <SendIcon />
                          )
                        }
                        sx={{
                          py: 1.5,
                          bgcolor: "primary.main",
                          "&:hover": {
                            bgcolor: "primary.dark",
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        {loading ? "Sending..." : "Send Message"}
                      </Button>
                    </Stack>
                  </form>

                  {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {error}
                    </Alert>
                  )}
                  {success && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      {success}
                    </Alert>
                  )}
                </Stack>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
